import React, { useMemo } from 'react';
import styled from 'styled-components';
import Icon, { IconProps } from '@minecraft.icon';
import {
  DesignSystemBgColor,
  DesignSystemBorderColor,
  DesignSystemBoxShadowColor,
  DesignSystemTextColor,
} from '@minecraft.themes';

const VARIANTS = {
  secondary: 'secondary',
  primary: 'primary',
  card: 'card',
} as const;

export type Variant = keyof typeof VARIANTS;
type Size = 'xxs' | 'xs' | 'sm' | 'md' | 'lg';

type ButtonColors = {
  bgColor: DesignSystemBgColor;
  bgColorHover: DesignSystemBgColor;
  bgColorActive: DesignSystemBgColor;
  textColor: DesignSystemTextColor;
  textColorHover: DesignSystemTextColor;
  textColorDisabled: DesignSystemTextColor;
  focusOutline: DesignSystemBoxShadowColor;
  borderColor: DesignSystemBorderColor;
  borderColorDisabled: DesignSystemBorderColor;
  borderWidth: number;
};

const BUTTON_STYLES_MAP: Record<Variant, ButtonColors> = {
  primary: {
    bgColor: 'cardLightContrast',
    bgColorActive: 'primaryActive',
    bgColorHover: 'primaryHover',
    textColor: 'default',
    textColorHover: 'inverted',
    textColorDisabled: 'disabled',
    focusOutline: 'cardFocus',
    borderColor: 'default',
    borderColorDisabled: 'default',
    borderWidth: 0,
  },
  secondary: {
    bgColor: 'card',
    bgColorActive: 'primaryActive',
    bgColorHover: 'primaryHover',
    textColor: 'primary',
    textColorHover: 'inverted',
    textColorDisabled: 'disabled',
    focusOutline: 'cardFocus',
    borderColor: 'primary',
    borderColorDisabled: 'default',
    borderWidth: 0.12,
  },
  card: {
    bgColor: 'card',
    bgColorActive: 'primaryActive',
    bgColorHover: 'primaryHover',
    textColor: 'default',
    textColorHover: 'inverted',
    textColorDisabled: 'disabled',
    focusOutline: 'cardFocus',
    borderColor: 'default',
    borderColorDisabled: 'default',
    borderWidth: 0,
  },
};

const SIZE_MAP: Record<Size, string> = {
  xxs: '1.5rem',
  xs: '2rem',
  sm: '2.25rem',
  md: '3.25rem',
  lg: '4.25rem',
};

export type CircleButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  label: string;
  icon?: string;
  variant?: Variant;
  size?: Size;
  /** pass a ref down to the underlying button element
   * this is used by the CircleDropdownButton so that the DropdownToggle
   * correctly opens & positions the dropdown menu
   * do not rename this prop!!
   */
  innerRef?: React.Ref<HTMLButtonElement>;
};

const StyledCircleButton = styled.button<{
  $disabled: boolean;
  $variant?: Variant;
  $size?: Size;
}>`
  display: flex;
  align-items: center;
  justify-content: center;

  border-style: solid;
  border-color: ${({ theme, $variant, $disabled }) =>
    $disabled
      ? theme.designSystem.borderColors[BUTTON_STYLES_MAP[$variant].borderColorDisabled]
      : theme.designSystem.borderColors[BUTTON_STYLES_MAP[$variant].borderColor]};
  border-width: ${({ $variant }) => `${BUTTON_STYLES_MAP[$variant].borderWidth}rem !important;`}

  border-radius: 3rem;
  height: ${({ $size }) => SIZE_MAP[$size]};
  width: ${({ $size }) => SIZE_MAP[$size]};
  padding: 0.2rem;

  background-color: ${({ theme, $variant }) => theme.designSystem.bgColors[BUTTON_STYLES_MAP[$variant].bgColor]};
  color: ${({ theme, $variant, $disabled }) =>
    $disabled
      ? theme.designSystem.textColors[BUTTON_STYLES_MAP[$variant].textColorDisabled]
      : theme.designSystem.textColors[BUTTON_STYLES_MAP[$variant].textColor]};

  &:hover {
    background-color: ${({ theme, $variant, $disabled }) =>
      $disabled
        ? theme.designSystem.bgColors[BUTTON_STYLES_MAP[$variant].bgColor]
        : theme.designSystem.bgColors[BUTTON_STYLES_MAP[$variant].bgColorHover]};
    color: ${({ theme, $variant, $disabled }) =>
      $disabled
        ? theme.designSystem.textColors[BUTTON_STYLES_MAP[$variant].textColorDisabled]
        : theme.designSystem.textColors[BUTTON_STYLES_MAP[$variant].textColorHover]};
  }

  &:active {
    background-color: ${({ theme, $variant, $disabled }) =>
      $disabled
        ? theme.designSystem.bgColors[BUTTON_STYLES_MAP[$variant].bgColor]
        : theme.designSystem.bgColors[BUTTON_STYLES_MAP[$variant].bgColorActive]};
    color: ${({ theme }) => theme.designSystem.textColors.inverted};
  }

  &:focus {
    outline: 0.25rem solid
      ${({ theme, $variant }) => theme.designSystem.boxShadows[BUTTON_STYLES_MAP[$variant].focusOutline]};
  }
`;

/**
 * the size prop for the button is not always the same size value
 * for the Icon, this maps the value of the CircleButton size to the Icon size
 */
const PROP_SIZE_TO_ICON_SIZE: Record<Size, IconProps['size']> = {
  xxs: 'ssm',
  xs: 'sm',
  sm: 'sm',
  md: 'md',
  lg: 'lg',
};

const validateVariant = (variantProp: string): Variant => {
  if (['secondary', 'primary', 'card'].includes(variantProp)) {
    return variantProp as Variant;
  }

  return VARIANTS.primary;
};

const validateSize = (sizeProp: string): Size => {
  if (['xxs', 'xs', 'sm', 'md', 'lg'].includes(sizeProp)) {
    return sizeProp as Size;
  }

  return 'sm';
};

export const CircleButton = ({
  icon,
  label,
  variant: variantProp = 'primary',
  size: sizeProp = 'sm',
  disabled = false,
  innerRef,
  ...props
}: CircleButtonProps) => {
  const { size, iconSize, variant } = useMemo(() => {
    const validatedSize = validateSize(sizeProp);
    return {
      size: validatedSize,
      iconSize: PROP_SIZE_TO_ICON_SIZE[validatedSize],
      variant: validateVariant(variantProp),
    };
  }, [sizeProp, variantProp]);
  return (
    <StyledCircleButton
      type="button"
      aria-label={label}
      $variant={variant}
      $size={size}
      $disabled={disabled}
      disabled={disabled}
      tabIndex={0}
      {...props}
      ref={innerRef}
    >
      {icon && <Icon name={icon} size={iconSize} inheritColor />}
    </StyledCircleButton>
  );
};
